import { find, uniq } from 'lodash';
import { checkFeatureFlags } from '../commands/featureFlagsCommands';
import {
  signupCustomRequestWithinCheckpointWithDynamicEndpoint,
  signupCustomRequestWithTemplatedPayload,
} from '../commands/signupCommands';
import { setTokenDetails } from '../commands/userCommands';
import { setDepositLimit } from '../commands/walletCommands';
import { auth } from '../data-types/initialisation/initialisationConstants';
import { Mode } from '../data-types/initialisation/initialisationEnums';
import { AcceptedCountries } from '../data-types/international/internationalEnums';
import { POST_HOG_EVENT } from '../data-types/posthog/posthogEnums';
import { betkwiffSignUpSteps, keSignUpSteps, STARTER_CHECKPOINTS, ukSignUpSteps } from '../data-types/signup/signupConstants';
import { PASSWORD_PATTERN, SIGNUP_CHECKPOINT, SIGNUP_FLOW, SIGNUP_TYPE } from '../data-types/signup/signupEnums';
import {
  SignupAddressPostcodeLookupEnforcedFields,
  SignupAddressRequiredFields,
  SignupCheckpoint,
  SignupCheckpointElement,
  SignupCheckpointElementOptions,
  SignupCheckpointPassed,
  SignupCredsPayload,
  SignupStepper,
} from '../data-types/signup/signupInterfaces';
import { ThirdPartyTrackerId } from '../data-types/trackers/trackersEnums';
import { TrackerOptionalParameters } from '../data-types/trackers/trackersInterfaces';
import { VALIDATION_TYPE } from '../data-types/validation/validationEnums';
import { PAYMENT_METHODS_TRANSACTION_TYPE_ID } from '../data-types/wallet/walletEnums';
import { PaymentMethodWithNoAccounts } from '../data-types/wallet/walletInterfaces';
import { fingerprint } from '../initialisation/initialisation';
import { isFirstDepositMandatoryForUserCountry } from '../international/internationalUtils';
import { PATHS } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { trackLoginWithCredentials, trackRegistrationFinished, trackSessionLogin, trackZimplerPnPFirstDeposit } from '../services/trackers';
import { getTransunionBlackBox } from '../services/transunion';
import {
  setCurrentSignUpPath,
  setHasJustSignedUp,
  setInitialSignUpLoad,
  setIsAfterZimplerSignup,
  setSignupCheckpoint,
  setSignUpDialogOpen,
} from '../state/actions/signupActions';
import { setAuthentication, setUserDetails } from '../state/actions/userDataActions';
import { store } from '../state/store';
import { isOfType } from '../validation/typeValidation';
import { postHogCapture, postHogStartSessionRecording } from './posthog';
import { enqueueSnackbarPrompt } from './prompts';
import { setXtremepushUserId } from './xtremepush';

export const postSuccessSignupActions = () => {
  setAuthentication(auth.AUTHENTICATED);
  setInitialSignUpLoad(true);
  setCurrentSignUpPath(PATHS.SIGNUP.STEPS.EMAIL);
  closeSignupDialogAndClearCheckpoint();
  setHasJustSignedUp(true);
  checkFeatureFlags();

  const {
    userData: { user },
  } = store.getState();

  if (user && typeof user.id === 'number') {
    trackSessionLogin(Number(user.id), user.email || '');
    trackLoginWithCredentials();
    removeLoggedInAfterSuccessSignup();
    setXtremepushUserId(user.id);
  }

  browserHistory.push(PATHS.APP.ROOT);
};

export const handleSignUpRedirectToDepositLimitOrDeposit = () => {
  if (isFirstDepositMandatoryForUserCountry()) {
    setCurrentSignUpPath(PATHS.SIGNUP.STEPS.DEPOSIT_LIMIT);
    browserHistory.push(PATHS.SIGNUP.STEPS.DEPOSIT_LIMIT);
  } else {
    handleSignUpNoDepositLimit();
    setCurrentSignUpPath(PATHS.SIGNUP.STEPS.DEPOSIT);
    browserHistory.push(PATHS.SIGNUP.STEPS.DEPOSIT);
  }
};

export const handleSignUpNoDepositLimit = async (): Promise<void> => {
  try {
    await setDepositLimit(undefined, null);
  } catch (e) {
    // do nothing
  }
};

export const handleJoin = () => {
  const { signupV3 } = store.getState().signup;
  const { useSignupV3 } = signupV3;

  postHogStartSessionRecording();
  if (useSignupV3) {
    setSignUpDialogOpen(true);
  } else {
    handleLegacyJoin();
  }
};

export const handleLegacyJoin = () => {
  const { authentication } = store.getState().userData;

  if (authentication !== auth.LOCATION_REJECTED) {
    startLegacyJoin();
  }
};

export const startLegacyJoin = () => {
  const signupSteps = getSignUpSteps();

  setAuthentication(auth.SIGNUP);
  postHogCapture(POST_HOG_EVENT.STARTED_REGISTRATION_LEGACY, {});

  if (signupSteps.length) {
    browserHistory.push(signupSteps[0].path);
  } else {
    browserHistory.push(PATHS.SIGNUP.ROOT);
  }
};

export const getSignUpSteps = (): SignupStepper[] => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.GREAT_BRITAIN:
      return ukSignUpSteps;
    case AcceptedCountries.KENYA:
      return keSignUpSteps;
    default:
      return betkwiffSignUpSteps;
  }
};

export const getPasswordPattern = (options?: SignupCheckpointElementOptions): PASSWORD_PATTERN =>
  options && isOfType(options.pattern, VALIDATION_TYPE.NUMBER) ? Number(options.pattern) : PASSWORD_PATTERN.FOUR_CHARS;

export const getElementFromCheckpointByType = (checkpoint: SignupCheckpoint | undefined, type: SIGNUP_TYPE) =>
  checkpoint && checkpoint.elements && find(checkpoint.elements, { type });

export const getPartialDynamicPayloadElements = (element: SignupCheckpointElement | undefined): SignupCredsPayload => {
  let payload = {};

  if (element && element.values && element.values.length) {
    element.values.forEach(value => {
      switch (value) {
        case 'uuid':
          payload = {
            ...payload,
            uuid: fingerprint,
          };
          break;
        case 'deviceRecognitionString':
          payload = {
            ...payload,
            deviceRecognitionString: getTransunionBlackBox(),
          };
          break;
        default:
          break;
      }
    });
  }

  return payload;
};

export const getPartialDynamicPayloadTemplateOptions = (options: string[]): object => {
  let payload = {};

  options.forEach(value => {
    switch (value) {
      default:
        payload = {
          ...payload,
        };
        break;
    }
  });

  return payload;
};

export const handleDepositSkip = () => {
  const timeout = setTimeout(() => {
    postSuccessSignupActions();
    clearTimeout(timeout);
  }, 0);
};

export const handleSignupDynamicEndpointRequest = async (checkpoint: SignupCheckpoint | undefined, payload: object = {}) => {
  try {
    if (checkpoint && checkpoint.endpoint) {
      const options =
        checkpoint.templateOptions && checkpoint.templateOptions.endpointValues ? checkpoint.templateOptions.endpointValues : [];
      const res = await signupCustomRequestWithTemplatedPayload(checkpoint.endpoint, payload, options);
      handlePostDetailsRegistrationActions(res);
    }
  } catch (e) {
    enqueueSnackbarPrompt(e.message, { closeOnClick: true });
  }
};

export const handlePostDetailsRegistrationActions = (res: SignupCheckpointPassed) => {
  const { user, nextCheckpoint, registrationFinished, sessionToken } = res;

  setUserDetails(user);

  if (sessionToken) {
    setTokenDetails(sessionToken);
    setLoggedInAfterSuccessSignup();
  }

  if (nextCheckpoint) {
    setSignupCheckpoint(nextCheckpoint);
  }

  if (registrationFinished) {
    postSuccessSignupActions();
    postHogCapture(POST_HOG_EVENT.COMPLETED_REGISTRATION, {});
  }
};

export const getRequiredStatusForAddressElements = (options: SignupCheckpointElementOptions | undefined): SignupAddressRequiredFields => {
  const postcodeRequired = options ? Boolean(options.postcodeRequired) : false;
  const buildingNumberRequired = options ? Boolean(options.buildingNameRequired) : false;
  const addressLine1Required = options ? Boolean(options.addressLine1Required) : false;
  const addressLine2Required = options ? Boolean(options.addressLine2Required) : false;
  const townRequired = options ? Boolean(options.townRequired) : false;
  const countyRequired = options ? Boolean(options.countyRequired) : false;

  return { postcodeRequired, buildingNumberRequired, addressLine1Required, addressLine2Required, townRequired, countyRequired };
};

export const getPostcodeLookupEnforcedStatusForAddressElements = (
  options: SignupCheckpointElementOptions | undefined,
): SignupAddressPostcodeLookupEnforcedFields => {
  const addressLine1PostCodeLookupEnforced = options ? Boolean(options.addressLine1PostCodeLookupEnforced) : false;
  const addressLine2PostCodeLookupEnforced = options ? Boolean(options.addressLine2PostCodeLookupEnforced) : false;
  const townPostCodeLookupEnforced = options ? Boolean(options.townPostCodeLookupEnforced) : false;
  const countyPostCodeLookupEnforced = options ? Boolean(options.countyRequiredPostCodeLookupEnforced) : false;

  return {
    addressLine1PostCodeLookupEnforced,
    addressLine2PostCodeLookupEnforced,
    townPostCodeLookupEnforced,
    countyPostCodeLookupEnforced,
  };
};

export const closeSignupDialogAndClearCheckpoint = () => {
  const { signupV3, dialogs } = store.getState().signup;
  const { signupDialogOpen } = dialogs;
  const { checkpoint } = signupV3;

  if (signupDialogOpen) {
    setSignUpDialogOpen(false);
  }

  if (checkpoint) {
    setSignupCheckpoint(undefined);
  }
};

export const hideActionButtons = (checkpoint: SignupCheckpoint | undefined) => {
  if (checkpoint && checkpoint.checkpoint === SIGNUP_CHECKPOINT.UK_ACCOUNT_VERIFICATION) {
    return true;
  }

  if (checkpoint && checkpoint.checkpoint === SIGNUP_CHECKPOINT.UK_OPTIONAL_OPT_IN) {
    return true;
  }

  if (checkpoint && checkpoint.checkpoint === SIGNUP_CHECKPOINT.IOM_OPTIONAL_OPT_IN) {
    return true;
  }

  if (checkpoint && checkpoint.checkpoint === SIGNUP_CHECKPOINT.BRAZIL_OPTIONAL_OPT_IN) {
    return true;
  }

  if (checkpoint && checkpoint.checkpoint === SIGNUP_CHECKPOINT.BRAZIL_CHOOSE_FLOW) {
    return true;
  }

  return false;
};

export const getSecondaryActionButtonTextId = (checkpoint: SignupCheckpoint | undefined, skippable: boolean, noLimitAllowed: boolean) => {
  if (skippable) {
    return 'common.later';
  }

  if (checkpoint && checkpoint.checkpoint === SIGNUP_CHECKPOINT.UK_SET_DEPOSIT_LIMIT && noLimitAllowed) {
    return 'common.no_limit';
  }

  return undefined;
};

export const getFormattedCPFNumber = (unformattedNumber: string) => {
  let formattedCpf = unformattedNumber.replace(/\D/g, ''); // Remove non-numeric characters

  if (formattedCpf.length > 3) {
    formattedCpf = `${formattedCpf.slice(0, 3)}.${formattedCpf.slice(3)}`;
  }
  if (formattedCpf.length > 7) {
    formattedCpf = `${formattedCpf.slice(0, 7)}.${formattedCpf.slice(7)}`;
  }
  if (formattedCpf.length > 11) {
    formattedCpf = `${formattedCpf.slice(0, 11)}-${formattedCpf.slice(11)}`;
  }

  return formattedCpf;
};

export const getSecondaryActionButtonMode = (skippable: boolean, noLimitAllowed: boolean) => {
  if (skippable) {
    return Mode.LIGHT;
  }

  if (noLimitAllowed) {
    return Mode.DARK;
  }

  return Mode.DARK;
};

export const shouldSkipPhoneVerificationCode = (options: SignupCheckpointElementOptions | undefined, userLocation: AcceptedCountries) =>
  options && options.verificationDisabledForCountries && options.verificationDisabledForCountries.includes(userLocation);

export const shouldSkipPhoneVerificationCodeFromCheckpoint = (checkpoint: SignupCheckpoint, userLocation: AcceptedCountries) => {
  const phoneElement = getElementFromCheckpointByType(checkpoint, SIGNUP_TYPE.PHONE_VERIFICATION);
  const skipPhoneVerificationCode = phoneElement && shouldSkipPhoneVerificationCode(phoneElement.options, userLocation);

  return skipPhoneVerificationCode;
};

export const isFirstCheckPoint = (checkpoint: SignupCheckpoint | undefined) =>
  checkpoint && STARTER_CHECKPOINTS.includes(checkpoint.checkpoint);

export const handleSignUpV3Tracking = (checkpoint: SignupCheckpoint | undefined, optionalParams: TrackerOptionalParameters = {}) => {
  const { userId } = optionalParams;

  if (checkpoint && checkpoint.thirdPartyTrackers) {
    for (const trackerType of checkpoint.thirdPartyTrackers) {
      switch (trackerType) {
        case ThirdPartyTrackerId.REGISTRATION_FINISHED:
          if (userId) {
            trackRegistrationFinished({ userId, fingerprint });
          }
          break;
        default:
          break;
      }
    }
  }
};

export const checkPostZimplerSignup = (balance: number) => {
  const isPostZimplerSignup = store.getState().signup.isAfterZimplerSignup;

  if (isPostZimplerSignup) {
    if (balance > 0) {
      trackZimplerPnPFirstDeposit(balance);
    }

    setIsAfterZimplerSignup(false);
  }
};

export const setLoggedInAfterSuccessSignup = () => localStorage.setItem('loggedInAfterSuccessSignup', '1');

export const hasLoggedInAfterSuccessSignup = () => localStorage.getItem('loggedInAfterSuccessSignup');

export const removeLoggedInAfterSuccessSignup = () => localStorage.removeItem('loggedInAfterSuccessSignup');

export const forcefullySkipFlowChoiceInBR = async (checkpoint: SignupCheckpoint) => {
  const { templateOptions } = checkpoint;
  const flows = templateOptions ? templateOptions.flows : [];
  const normalFlow = find(flows, { name: SIGNUP_FLOW.BR_NORMAL });
  if (normalFlow) {
    await handleSignupDynamicEndpointRequest(checkpoint, normalFlow.payload);
  } else {
    setSignupCheckpoint(checkpoint);
  }
};

export const handleStartCheckpoint = async (checkpoint: SignupCheckpoint) => {
  switch (checkpoint.checkpoint) {
    case SIGNUP_CHECKPOINT.BRAZIL_CHOOSE_FLOW:
      await forcefullySkipFlowChoiceInBR(checkpoint);
      break;
    default:
      setSignupCheckpoint(checkpoint);
      break;
  }
};

export const loadAvailableCurrencies = async (
  checkpoint: SIGNUP_CHECKPOINT,
  endpoint: string,
  onSuccess: (currencyISOs: string[]) => void,
  onError: () => void,
) => {
  try {
    const allPaymentMethods = (await signupCustomRequestWithinCheckpointWithDynamicEndpoint(checkpoint, endpoint, {
      transactionType: PAYMENT_METHODS_TRANSACTION_TYPE_ID.DEPOSIT,
    })) as PaymentMethodWithNoAccounts[] | undefined;
    const availableMethods = (allPaymentMethods && allPaymentMethods.filter(method => method.isActive)) || [];
    const currencyISOs = uniq(availableMethods.map(method => method.currency).filter(currency => currency));

    onSuccess(currencyISOs);
  } catch (_) {
    onError();
  }
};
